import { type ApolloClient } from "@apollo/client";
import { type SliceWallState } from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import {
  getLocationActiveProfile,
  getMedia,
  getMediaType,
  getProfile,
} from "../utils/wall-utils-gql";

const DEFAULT_IMAGE_DURATION_MS = 30 * 1000;
const DEFAULT_RESPONSE: SliceWallState["background"] = {
  active: false,
  audio: false,
  loop: false,
  type: "image",
};

type BackgroundEngineEvents = {
  "background-updated": (update: SliceWallState["background"]) => void;
};

export class WallEngineBackground extends EventEmitter<BackgroundEngineEvents> {
  private timeoutPlayingMedia?: ReturnType<typeof setTimeout>;

  private activating: boolean = false;
  private mediaIndex: number = -1;

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly location?: string,
    private readonly profile?: string,
  ) {
    super();

    if (!this.location && !this.profile) {
      throw new Error("A location or profile is required");
    }
  }

  public destroy(): void {
    super.destroy();
    this.cancelTimeoutPlayingMedia();
    console.log("[wall-engine-background] Destroyed");
  }

  public async activate(): Promise<void> {
    if (this.activating) {
      return;
    }
    this.activating = true;
    console.log("[wall-engine-background] Activating...");

    this.emit("background-updated", await this.handleMedia());

    console.log("[wall-engine-background] Activated");
    this.activating = false;
  }

  public disable(): void {
    this.cancelTimeoutPlayingMedia();

    this.emit("background-updated", DEFAULT_RESPONSE);
  }

  private cancelTimeoutPlayingMedia() {
    if (this.timeoutPlayingMedia) {
      clearTimeout(this.timeoutPlayingMedia);
      this.timeoutPlayingMedia = undefined;
      console.log("[wall-engine-background] Cancelled playing media timeout");
    }
  }

  private async handleMedia(): Promise<SliceWallState["background"]> {
    this.cancelTimeoutPlayingMedia();

    const profile = this.profile
      ? await getProfile(this.client, this.profile)
      : await getLocationActiveProfile(this.client, this.location!);

    if (profile?.background?.backgroundMedia?.length) {
      if (profile.background.backgroundMedia.length > 1) {
        return await this.handleMediaMultiple(
          profile.background.backgroundMedia,
        );
      } else {
        return await this.handleMediaSingle(
          profile.background.backgroundMedia[0],
        );
      }
    }

    return DEFAULT_RESPONSE;
  }

  private async handleMediaSingle(
    id: string,
  ): Promise<SliceWallState["background"]> {
    this.cancelTimeoutPlayingMedia();

    const media = await getMedia(this.client, id);
    if (media) {
      console.log("[wall-engine-background] Playing media:", id);

      return {
        active: true,
        audio: false,
        loop: true,
        media,
        type: getMediaType(media),
      };
    }

    return DEFAULT_RESPONSE;
  }

  private async handleMediaMultiple(
    ids: string[],
  ): Promise<SliceWallState["background"]> {
    this.cancelTimeoutPlayingMedia();

    this.mediaIndex =
      this.mediaIndex >= ids.length - 1 ? 0 : this.mediaIndex + 1;

    const mediaId = ids[this.mediaIndex];
    const media = await getMedia(this.client, mediaId);
    if (media) {
      const mediaType = getMediaType(media);
      if (mediaType === "image") {
        this.timeoutPlayingMedia = setTimeout(
          () => this.activate(),
          DEFAULT_IMAGE_DURATION_MS,
        );
      }

      console.log("[wall-engine-background] Playing media:", mediaId);

      return {
        active: true,
        audio: false,
        loop: false,
        media,
        type: mediaType,
      };
    }

    return DEFAULT_RESPONSE;
  }
}
